import { useEffect, useRef, useState } from "react";
import { Mic, Moon, PlusCircle, Volume1 } from "lucide-react";
import { toast } from "sonner";
import { api, errMessage } from "../../lib/api";
import { setPipVolume, setVoiceSpeed, VOICE_SPEEDS, setAmbienceVolume } from "../../lib/pipVolume";

const LANG_LABELS = { es: "Spanish" };
const LOOK_LABELS = {
  bird: "Pip", cat: "Coco", rabbit: "Sunny",
  turtle: "Momo", dog: "Luna", fish: "Bubbles",
};

const CAPS = [15, 30, 45, 60];
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hourLabel = (h) => `${((h + 11) % 12) + 1}${h < 12 ? " am" : " pm"}`;

const Donut = ({ used, capSeconds, capped }) => {
  const pct = Math.min(1, capSeconds ? used / capSeconds : 0);
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <svg
      viewBox="0 0 64 64"
      className="w-16 h-16 shrink-0"
      role="img"
      aria-label={`${Math.round(used / 60)} of ${capSeconds / 60} voice minutes used`}
    >
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.12"
        strokeWidth="7"
      />
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        stroke={capped ? "#ef4444" : pct > 0.8 ? "#f59e0b" : "#10b981"}
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={`${c * pct} ${c}`}
        transform="rotate(-90 32 32)"
      />
      <text
        x="32"
        y="30"
        textAnchor="middle"
        fontSize="13"
        fontWeight="800"
        fill="currentColor"
      >
        {Math.round(used / 60)}
      </text>
      <text
        x="32"
        y="43"
        textAnchor="middle"
        fontSize="8"
        fontWeight="600"
        fill="currentColor"
        opacity="0.6"
      >
        of {capSeconds / 60} min
      </text>
    </svg>
  );
};

// Parent-Controlled Usage Guard: per-child daily AI-voice budget with a live
// donut meter. Enforced server-side — this panel just sets & shows it.
export const VoiceUsageGuard = () => {
  const [kids, setKids] = useState([]);
  const volTimers = useRef({});
  const previewCache = useRef({});
  const previewAudio = useRef(null);
  const [usage, setUsage] = useState({});

  const loadUsage = (list) => {
    list.forEach((k) =>
      api
        .get(`/children/${k.child_id}/voice-usage`)
        .then(({ data }) => setUsage((u) => ({ ...u, [k.child_id]: data })))
        .catch(() => {}),
    );
  };

  useEffect(() => {
    let timer;
    api
      .get("/children")
      .then(({ data }) => {
        setKids(data);
        loadUsage(data);
        // Midnight reset follows each child's local timezone — sync it silently.
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        data.forEach((k) => {
          if (tz && k.timezone !== tz) {
            api
              .patch(`/children/${k.child_id}`, { timezone: tz })
              .catch(() => {});
          }
        });
        timer = setInterval(() => loadUsage(data), 60000); // real-time-ish refresh
      })
      .catch(() => {});
    return () => clearInterval(timer);
  }, []);

  const setCap = async (kid, minutes) => {
    try {
      await api.patch(`/children/${kid.child_id}`, {
        voice_cap_minutes: minutes,
      });
      setKids((ks) =>
        ks.map((k) =>
          k.child_id === kid.child_id
            ? { ...k, voice_cap_minutes: minutes }
            : k,
        ),
      );
      loadUsage([kid]);
      toast.success(
        `${kid.display_name}'s daily voice time set to ${minutes} minutes`,
      );
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const grantBonus = async (kid) => {
    try {
      const { data } = await api.post(`/children/${kid.child_id}/voice-bonus`);
      loadUsage([kid]);
      toast.success(
        `+10 bonus voice minutes for ${kid.display_name} today (${Math.round((data.bonus_seconds || 600) / 60)} min total bonus)`,
      );
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const setQuietHours = async (kid, patch) => {
    const prev = kid.quiet_hours || { enabled: false, start: 20, end: 7 };
    const qh = { enabled: false, start: 20, end: 7, ...prev, ...patch };
    // Optimistic: flip the checkbox instantly, revert if the save fails.
    setKids((ks) =>
      ks.map((k) =>
        k.child_id === kid.child_id ? { ...k, quiet_hours: qh } : k,
      ),
    );
    try {
      await api.patch(`/children/${kid.child_id}`, { quiet_hours: qh });
      // Let the dashboard refetch so ChildCard's quiet-hours line stays in sync.
      window.dispatchEvent(new Event("children-updated"));
      toast.success(
        qh.enabled
          ? `Quiet hours on for ${kid.display_name}: ${hourLabel(qh.start)} – ${hourLabel(qh.end)}`
          : `Quiet hours off for ${kid.display_name}`,
      );
    } catch (e) {
      setKids((ks) =>
        ks.map((k) =>
          k.child_id === kid.child_id ? { ...k, quiet_hours: prev } : k,
        ),
      );
      toast.error(errMessage(e));
    }
  };

  const setScreenCap = async (kid, minutes) => {
    try {
      await api.patch(`/children/${kid.child_id}`, {
        screen_cap_minutes: minutes,
      });
      setKids((ks) =>
        ks.map((k) =>
          k.child_id === kid.child_id
            ? { ...k, screen_cap_minutes: minutes }
            : k,
        ),
      );
      loadUsage([kid]);
      toast.success(
        minutes
          ? `${kid.display_name}'s total app time set to ${minutes} minutes a day`
          : `Total app time limit turned off for ${kid.display_name}`,
      );
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (!kids.length) return null;
  const saveVolume = async (kid, v) => {
    setKids((ks) => ks.map((k) => (k.child_id === kid.child_id ? { ...k, pip_volume: v } : k)));
    setPipVolume(kid.child_id, v); // applies instantly to any live voice graph
    clearTimeout(volTimers.current[kid.child_id]);
    volTimers.current[kid.child_id] = setTimeout(async () => {
      try {
        await api.patch(`/children/${kid.child_id}`, { pip_volume: v });
        toast.success(`Pip's voice set to ${v}% for ${kid.display_name}`);
      } catch (e) { toast.error(errMessage(e)); }
    }, 600);
  };

  // Safari background bed volume (0 = muted): debounced like Pip's voice.
  const saveAmbience = async (kid, v) => {
    setKids((ks) => ks.map((k) => (k.child_id === kid.child_id ? { ...k, ambience_volume: v } : k)));
    setAmbienceVolume(kid.child_id, v); // applies instantly on the next habitat
    clearTimeout(volTimers.current[`amb_${kid.child_id}`]);
    volTimers.current[`amb_${kid.child_id}`] = setTimeout(async () => {
      try {
        await api.patch(`/children/${kid.child_id}`, { ambience_volume: v });
        toast.success(v === 0
          ? `Safari background muted for ${kid.display_name}`
          : `Safari background set to ${v}% for ${kid.display_name}`);
      } catch (e) { toast.error(errMessage(e)); }
    }, 600);
  };

  // Global voice speed: applies to EVERY ElevenLabs line Pip speaks
  // (speech practice, math quests, story time, navigation labels).
  // A short coach sample plays instantly at the tapped pace.
  const playSpeedSample = async (kid, s) => {
    try {
      const look = kid.coach?.look || "rabbit";
      if (!previewCache.current[look]) {
        const { data } = await api.post("/voice/coach-preview", { look });
        if (data.status !== "ok") return;
        previewCache.current[look] = data;
      }
      const d = previewCache.current[look];
      previewAudio.current?.pause();
      const a = new Audio(`data:${d.mime};base64,${d.data_b64}`);
      a.playbackRate = s;
      previewAudio.current = a;
      a.play().catch(() => {});
    } catch { /* sample is a bonus */ }
  };

  const saveSpeed = async (kid, s) => {
    setKids((ks) => ks.map((k) => (k.child_id === kid.child_id ? { ...k, voice_speed: s } : k)));
    setVoiceSpeed(kid.child_id, s); // applies instantly to the next spoken line
    playSpeedSample(kid, s); // hear the pace right away
    try {
      await api.patch(`/children/${kid.child_id}`, { voice_speed: s });
      toast.success(`Pip now talks at ${s}x for ${kid.display_name}`);
    } catch (e) { toast.error(errMessage(e)); }
  };

  // Language Voice Preview: hear a quick hello in that language, in the
  // picked coach's voice, right when a parent selects it.
  const playLanguageSample = async (look, lg) => {
    try {
      const key = `${look}-${lg}`;
      if (!previewCache.current[key]) {
        const { data } = await api.post("/voice/coach-preview", { look, language: lg });
        if (data.status !== "ok") return;
        previewCache.current[key] = data;
      }
      const d = previewCache.current[key];
      previewAudio.current?.pause();
      const a = new Audio(`data:${d.mime};base64,${d.data_b64}`);
      previewAudio.current = a;
      a.play().catch(() => {});
    } catch { /* sample is a bonus */ }
  };

  // Per Language Voices: a different coach look can speak ES/DE/FR lines.
  const saveLanguageVoice = async (kid, langKey, look) => {
    const merged = { ...(kid.language_voices || {}) };
    if (look) merged[langKey] = look; else delete merged[langKey];
    setKids((ks) => ks.map((k) => (k.child_id === kid.child_id ? { ...k, language_voices: merged } : k)));
    if (look) playLanguageSample(look, langKey); // hear the hello right away
    try {
      await api.patch(`/children/${kid.child_id}`, { language_voices: merged });
      toast.success(look
        ? `${LOOK_LABELS[look]} now speaks ${LANG_LABELS[langKey]} for ${kid.display_name}`
        : `${LANG_LABELS[langKey]} back to ${kid.display_name}'s main coach`);
    } catch (e) { toast.error(errMessage(e)); }
  };
  return (
    <div
      className="bg-card rounded-2xl border border-border p-5 space-y-4"
      data-testid="voice-usage-guard"
    >
      <div>
        <h3 className="text-sm font-bold font-display flex items-center gap-2">
          <Mic className="h-4 w-4 text-secondary" aria-hidden="true" /> Daily
          voice time
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Pip's AI voice pauses at the daily limit and switches to Visual Mode
          (text, mouth guides and games keep working). Enforced on our servers —
          resets at midnight.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {kids.map((k) => {
          const u = usage[k.child_id];
          const capMin = k.voice_cap_minutes || u?.cap_minutes || 30;
          return (
            <div
              key={k.child_id}
              className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5"
              data-testid={`voice-guard-${k.child_id}`}
            >
              <Donut
                used={u?.used_seconds || 0}
                capSeconds={u?.budget_seconds || capMin * 60}
                capped={Boolean(u?.capped)}
              />
              <div className="min-w-0">
                <p className="text-sm font-bold truncate">{k.display_name}</p>
                <select
                  value={capMin}
                  onChange={(e) => setCap(k, Number(e.target.value))}
                  className="mt-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold"
                  aria-label={`Daily voice cap for ${k.display_name}`}
                  data-testid={`voice-cap-select-${k.child_id}`}
                >
                  {CAPS.map((m) => (
                    <option key={m} value={m}>{`${m} min voice${m === 30 ? " (default)" : ""}`}</option>
                  ))}
                </select>
                <select
                  value={k.screen_cap_minutes || u?.screen_cap_minutes || 0}
                  onChange={(e) => setScreenCap(k, Number(e.target.value))}
                  className="mt-1 ml-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold"
                  aria-label={`Daily total app time for ${k.display_name}`}
                  data-testid={`screen-cap-select-${k.child_id}`}
                >
                  <option value={0}>App time: off</option>
                  {[30, 60, 90, 120].map((m) => (
                    <option key={m} value={m}>{`${m} min app total`}</option>
                  ))}
                </select>
                {(u?.screen_cap_minutes || 0) > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    App time today:{" "}
                    {Math.round((u?.screen_used_seconds || 0) / 60)} of{" "}
                    {u.screen_cap_minutes} min
                    {u?.screen_capped ? " — done for today" : ""}
                  </p>
                )}
                <div
                  className="mt-1.5 flex items-center gap-1.5"
                  data-testid={`pip-volume-${k.child_id}`}
                >
                  <Volume1 className="h-3.5 w-3.5 text-sky-500 shrink-0" aria-hidden="true" />
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="10"
                    value={k.pip_volume || 100}
                    onChange={(e) => saveVolume(k, Number(e.target.value))}
                    className="w-24 accent-sky-500"
                    aria-label={`Pip voice volume for ${k.display_name}`}
                    data-testid={`pip-volume-slider-${k.child_id}`}
                  />
                  <span className="text-[11px] font-semibold text-muted-foreground tabular-nums">
                    {k.pip_volume || 100}%{(k.pip_volume || 100) <= 30 ? " · whisper" : ""}
                  </span>
                </div>
                <div
                  className="mt-1.5 flex items-center gap-1.5"
                  data-testid={`ambience-volume-${k.child_id}`}
                >
                  <span className="text-[11px] font-semibold text-muted-foreground shrink-0">Safari background:</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="10"
                    value={k.ambience_volume ?? 50}
                    onChange={(e) => saveAmbience(k, Number(e.target.value))}
                    className="w-24 accent-emerald-500"
                    aria-label={`Safari background volume for ${k.display_name}`}
                    data-testid={`ambience-volume-slider-${k.child_id}`}
                  />
                  <span className="text-[11px] font-semibold text-muted-foreground tabular-nums">
                    {(k.ambience_volume ?? 50) === 0 ? "muted" : `${k.ambience_volume ?? 50}%`}
                  </span>
                </div>
                <div
                  className="mt-1.5 flex items-center gap-1.5"
                  data-testid={`voice-speed-${k.child_id}`}
                >
                  <span className="text-[11px] font-semibold text-muted-foreground">Voice speed:</span>
                  {VOICE_SPEEDS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => saveSpeed(k, s)}
                      aria-pressed={(k.voice_speed || 1) === s}
                      className={`rounded-full px-2 py-0.5 text-[11px] font-bold border transition-colors ${
                        (k.voice_speed || 1) === s
                          ? "bg-sky-500 text-white border-sky-500"
                          : "bg-background text-muted-foreground border-border hover:border-sky-300"
                      }`}
                      data-testid={`voice-speed-${s === 0.75 ? "slow" : s === 1 ? "normal" : "fast"}-${k.child_id}`}
                    >
                      {s}x{s === 1 ? "" : s < 1 ? " · calm" : " · zippy"}
                    </button>
                  ))}
                </div>
                <div
                  className="mt-1.5 flex items-center gap-1.5 flex-wrap"
                  data-testid={`language-voices-${k.child_id}`}
                >
                  <span className="text-[11px] font-semibold text-muted-foreground">Language voices:</span>
                  {["es"].map((lg) => (
                    <label key={lg} className="flex items-center gap-1 text-[11px] font-semibold">
                      {LANG_LABELS[lg]}
                      <select
                        value={(k.language_voices || {})[lg] || ""}
                        onChange={(e) => saveLanguageVoice(k, lg, e.target.value)}
                        className="rounded-md border border-border bg-background px-1 py-0.5 text-[11px]"
                        data-testid={`language-voice-${lg}-${k.child_id}`}
                      >
                        <option value="">Same as coach</option>
                        {Object.entries(LOOK_LABELS).map(([look, name]) => (
                          <option key={look} value={look}>{name}</option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
                <div
                  className="mt-1.5 flex items-center gap-1.5 flex-wrap"
                  data-testid={`quiet-hours-${k.child_id}`}
                >
                  <label className="flex items-center gap-1 text-[11px] font-semibold cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(k.quiet_hours?.enabled)}
                      onChange={(e) =>
                        setQuietHours(k, { enabled: e.target.checked })
                      }
                      className="accent-indigo-500"
                      data-testid={`quiet-hours-toggle-${k.child_id}`}
                    />
                    <Moon
                      className="h-3 w-3 text-indigo-500"
                      aria-hidden="true"
                    />{" "}
                    Quiet hours
                  </label>
                  {k.quiet_hours?.enabled && (
                    <>
                      <select
                        value={k.quiet_hours?.start ?? 20}
                        onChange={(e) =>
                          setQuietHours(k, { start: Number(e.target.value) })
                        }
                        className="rounded-md border border-border bg-background px-1 py-0.5 text-[11px] font-semibold"
                        aria-label={`Quiet hours start for ${k.display_name}`}
                        data-testid={`quiet-start-${k.child_id}`}
                      >
                        {HOURS.map((h) => (
                          <option key={h} value={h}>
                            {hourLabel(h)}
                          </option>
                        ))}
                      </select>
                      <span className="text-[11px] text-muted-foreground">
                        to
                      </span>
                      <select
                        value={k.quiet_hours?.end ?? 7}
                        onChange={(e) =>
                          setQuietHours(k, { end: Number(e.target.value) })
                        }
                        className="rounded-md border border-border bg-background px-1 py-0.5 text-[11px] font-semibold"
                        aria-label={`Quiet hours end for ${k.display_name}`}
                        data-testid={`quiet-end-${k.child_id}`}
                      >
                        {HOURS.map((h) => (
                          <option key={h} value={h}>
                            {hourLabel(h)}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
                {u?.capped ? (
                  <div className="mt-1">
                    <p
                      className="text-[10px] font-bold text-red-600"
                      data-testid={`voice-capped-${k.child_id}`}
                    >
                      Visual Mode active — voice resumes tomorrow
                    </p>
                    <button
                      type="button"
                      onClick={() => grantBonus(k)}
                      className="mt-1 inline-flex items-center gap-1 rounded-full border border-emerald-400 text-emerald-700 text-[11px] font-bold px-2.5 py-1 hover:bg-emerald-50 transition-colors"
                      data-testid={`voice-bonus-${k.child_id}`}
                    >
                      <PlusCircle className="h-3 w-3" aria-hidden="true" /> +10
                      bonus minutes today
                    </button>
                  </div>
                ) : u?.warning ? (
                  <p
                    className="text-[10px] font-bold text-amber-600 mt-1"
                    data-testid={`voice-warning-${k.child_id}`}
                  >
                    Just a few minutes of voice time left today
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
